import { ReactNode, useCallback, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { WizardProgress } from './WizardProgress';
import { WizardNavigation } from './WizardNavigation';
import { useWizardStore, useWizardActions } from '@/stores';
import { WIZARD_STEPS, type WizardStepId } from '@/lib/validations';

interface WizardContainerProps {
  children: ReactNode;
  stepValidation: Record<WizardStepId, boolean>;
  onSubmit: () => void;
  isSubmitting: boolean;
}

export function WizardContainer({
  children,
  stepValidation,
  onSubmit,
  isSubmitting,
}: WizardContainerProps) {
  const navigate = useNavigate();
  const step = useWizardStore((s) => s.step);
  const { setStep, reset } = useWizardActions();

  const currentStepConfig = WIZARD_STEPS.find((s) => s.id === step);
  const totalSteps = WIZARD_STEPS.length;
  const isLastStep = step === totalSteps;

  // Calculate completed steps
  const completedSteps = WIZARD_STEPS
    .filter((s) => s.id < step && stepValidation[s.id])
    .map((s) => s.id);

  const canGoBack = step > 1;
  const canGoForward = stepValidation[step as WizardStepId] ?? false;

  const handleBack = useCallback(() => {
    if (step > 1) {
      setStep(step - 1);
    }
  }, [step, setStep]);

  const handleNext = useCallback(() => {
    if (step < totalSteps && canGoForward) {
      setStep(step + 1);
    }
  }, [step, totalSteps, canGoForward, setStep]);

  const handleCancel = useCallback(() => {
    reset();
    navigate({ to: '/' });
  }, [reset, navigate]);

  // Handle keyboard navigation
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      // Don't trigger if user is typing in an input
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      ) {
        return;
      }

      if (e.key === 'Escape') {
        // Let the dialog handle this
        return;
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div className="container max-w-4xl py-8">
      <WizardProgress
        currentStep={step as WizardStepId}
        completedSteps={completedSteps}
      />

      <Card>
        <CardHeader>
          <CardTitle>{currentStepConfig?.title ?? `Step ${step}`}</CardTitle>
          <CardDescription>
            {currentStepConfig?.description ?? ''}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {children}

          <WizardNavigation
            currentStep={step}
            totalSteps={totalSteps}
            canGoBack={canGoBack}
            canGoForward={canGoForward}
            isSubmitting={isSubmitting}
            isLastStep={isLastStep}
            onBack={handleBack}
            onNext={handleNext}
            onSubmit={onSubmit}
            onCancel={handleCancel}
          />
        </CardContent>
      </Card>
    </div>
  );
}
